import React from 'react';
import { 
  UserGroupIcon, 
  BuildingOfficeIcon, 
  AcademicCapIcon,
  CogIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const Structure = () => {
  const organizationalLevels = [
    {
      level: "ზედა ხელმძღვანელობა",
      positions: [
        { title: "პრეზიდენტი", name: "გიორგი ლომია", icon: UserGroupIcon },
        { title: "გენერალური დირექტორი", name: "ალექსანდრე კახაბერი", icon: BuildingOfficeIcon },
        { title: "სპორტული დირექტორი", name: "ნიკა ქაცარავა", icon: AcademicCapIcon }
      ]
    },
    {
      level: "დეპარტამენტები",
      positions: [
        { title: "ფინანსური დეპარტამენტი", name: "ლევან ბერია", icon: ChartBarIcon },
        { title: "მარკეტინგის დეპარტამენტი", name: "გიორგი ჯიქია", icon: CogIcon },
        { title: "ადმინისტრაციული დეპარტამენტი", name: "ვალერი ყაზაიშვილი", icon: BuildingOfficeIcon },
        { title: "ტექნიკური დეპარტამენტი", name: "დავით ჩაკვეტაძე", icon: CogIcon }
      ]
    }
  ];

  const departments = [
    {
      name: "სპორტული დეპარტამენტი",
      description: "პასუხისმგებელია გუნდის სპორტული საქმიანობის მართვაზე",
      subDepartments: [
        "პირველი გუნდი",
        "ახალგაზრდული აკადემია",
        "სამწვრთნელო შტაბი",
        "სპორტის მედიცინა"
      ],
      color: "bg-blue-500"
    },
    {
      name: "ფინანსური დეპარტამენტი",
      description: "მართავს კლუბის ფინანსურ საქმიანობას",
      subDepartments: [
        "ბიუჯეტის დაგეგმვა",
        "აუდიტი",
        "კონტრაქტების მართვა",
        "სააღრიცხვო აღრიცხვა"
      ],
      color: "bg-green-500"
    },
    {
      name: "მარკეტინგის დეპარტამენტი",
      description: "პასუხისმგებელია ბრენდის მართვაზე და მარკეტინგზე",
      subDepartments: [
        "ბრენდ მენეჯმენტი",
        "სპონსორობა",
        "მედია ურთიერთობები",
        "ღონისძიებების ორგანიზება"
      ],
      color: "bg-purple-500"
    },
    {
      name: "ადმინისტრაციული დეპარტამენტი",
      description: "მართავს კლუბის ადმინისტრაციულ საქმიანობას",
      subDepartments: [
        "პერსონალის მართვა",
        "ოფისის მართვა",
        "კონტრაქტების მომზადება",
        "დოკუმენტაცია"
      ],
      color: "bg-yellow-500"
    },
    {
      name: "ტექნიკური დეპარტამენტი",
      description: "პასუხისმგებელია ტექნიკური მხარდაჭერისთვის",
      subDepartments: [
        "სტადიონის მართვა",
        "IT სისტემები",
        "უსაფრთხოება",
        "ტექნიკური მომსახურება"
      ],
      color: "bg-red-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            სტრუქტურა
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            კლუბის ორგანიზაციული სტრუქტურა და დეპარტამენტები
          </p>
        </div>

        {/* Organizational Levels */}
        <div className="space-y-12 mb-16">
          {organizationalLevels.map((level, levelIndex) => (
            <div key={levelIndex} className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
                {level.level}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {level.positions.map((position, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center mb-4">
                      <div className="p-3 bg-primary-100 rounded-full mr-4">
                        <position.icon className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 font-georgian">
                          {position.title}
                        </h3>
                        <p className="text-primary-600 font-georgian">
                          {position.name}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Departments */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
            დეპარტამენტები
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {departments.map((dept, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start mb-4">
                  <div className={`${dept.color} p-3 rounded-lg mr-4`}>
                    <BuildingOfficeIcon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                      {dept.name}
                    </h3>
                    <p className="text-gray-600 font-georgian text-sm">
                      {dept.description}
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-900 font-georgian mb-3">
                    ქვედეპარტამენტები:
                  </h4>
                  <ul className="space-y-1">
                    {dept.subDepartments.map((subDept, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600 font-georgian">
                        <span className="w-1.5 h-1.5 bg-primary-600 rounded-full mr-2"></span>
                        {subDept}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Organizational Chart */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-8">
            ორგანიზაციული სქემა
          </h2>
          <div className="bg-gray-50 rounded-lg p-8 overflow-x-auto">
            <div className="min-w-full">
              {/* President Level */}
              <div className="text-center mb-8">
                <div className="bg-primary-600 text-white rounded-lg p-4 max-w-xs mx-auto">
                  <h3 className="font-semibold font-georgian">პრეზიდენტი</h3>
                  <p className="text-sm font-georgian">ვლადიმერ ფალიაშვილი</p>
                </div>
              </div>

              {/* Directors Level */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-green-600 text-white rounded-lg p-3 text-center">
                  <h4 className="font-semibold text-sm font-georgian">დირექტორი</h4>
                  <p className="text-xs font-georgian">ივანე ხარაზიშვილი</p>
                </div>
                <div className="bg-purple-600 text-white rounded-lg p-3 text-center">
                  <h4 className="font-semibold text-sm font-georgian">ლიცენზიის მენეჯერი</h4>
                  <p className="text-xs font-georgian">შალვა ღონღაძე</p>
                </div>
                <div className="bg-blue-600 text-white rounded-lg p-3 text-center">
                  <h4 className="font-semibold text-sm font-georgian">ფინანსისტი</h4>
                  <p className="text-xs font-georgian">მარინა მჭედლიძე</p>
                </div>
              </div>

              {/* Departments Level */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div className="bg-gray-600 text-white rounded-lg p-2 text-center">
                  <p className="font-semibold text-xs font-georgian">მედია</p>
                  <p className="text-xs font-georgian">დიმიტრი ზავარინი</p>
                </div>
                <div className="bg-gray-600 text-white rounded-lg p-2 text-center">
                  <p className="font-semibold text-xs font-georgian">უსაფრთხოება</p>
                  <p className="text-xs font-georgian">მერაბი ჩხეიძე</p>
                </div>
                <div className="bg-gray-600 text-white rounded-lg p-2 text-center">
                  <p className="font-semibold text-xs font-georgian">გულშემატკივარი</p>
                  <p className="text-xs font-georgian">გიორგი აბელიანი</p>
                </div>
                <div className="bg-gray-600 text-white rounded-lg p-2 text-center">
                  <p className="font-semibold text-xs font-georgian">სოციალური</p>
                  <p className="text-xs font-georgian">პაატა ქიმერიძე</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Structure;
